/**
 * CORE-M-01 Management category bar.
 *
 * The one horizontal nav Chans's spec allows inside Management (G-4 exception,
 * called out in §2.1). Sits at the top of the Management page, orders its
 * seven tabs the way an actual PPC review goes left to right, and carries a
 * red open-item count badge per tab (G-10).
 *
 * Selected tab is kept in the URL query string (`?tab=<slug>`) so a
 * refresh lands back on the same tab.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CountBadge } from '@/components/google-ads/CountBadge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import {
  DEFAULT_MANAGEMENT_TAB,
  MANAGEMENT_TABS,
  isManagementTabSlug,
  type ManagementTabSlug,
} from './tabSlugs';
import { useManagementCounts } from './useManagementCounts';

interface ManagementCategoryBarProps {
  tenantId: string | null;
  activeTab?: ManagementTabSlug;
  onTabChange?: (slug: ManagementTabSlug) => void;
  className?: string;
}

// CORE-G-10: the per-tab count pill is the shared <CountBadge> primitive
// at `components/google-ads/CountBadge`. It renders null when count is
// zero, so a bar of quiet tabs stays clean.

/**
 * Read the active tab from the URL query string, defaulting to
 * DEFAULT_MANAGEMENT_TAB when the param is absent or unknown.
 */
export function useManagementTabFromUrl(): [
  ManagementTabSlug,
  (slug: ManagementTabSlug) => void,
] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const active: ManagementTabSlug = isManagementTabSlug(raw)
    ? raw
    : DEFAULT_MANAGEMENT_TAB;

  const setActive = useCallback(
    (slug: ManagementTabSlug) => {
      const next = new URLSearchParams(params);
      next.set('tab', slug);
      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  return [active, setActive];
}

export function ManagementCategoryBar({
  tenantId,
  activeTab,
  onTabChange,
  className,
}: ManagementCategoryBarProps) {
  const counts = useManagementCounts(tenantId);
  const [urlTab, setUrlTab] = useManagementTabFromUrl();
  const value = activeTab ?? urlTab;

  const handleChange = useCallback(
    (raw: string) => {
      if (!isManagementTabSlug(raw)) return;
      if (onTabChange) onTabChange(raw);
      else setUrlTab(raw);
    },
    [onTabChange, setUrlTab],
  );

  const tabs = useMemo(() => MANAGEMENT_TABS, []);

  return (
    <Tabs value={value} onValueChange={handleChange} className={className}>
      <TabsList
        aria-label="Management categories"
        className={cn(
          // Override the pill-shape default from the ui primitive: this bar
          // reads as a top-of-page nav strip, not a form segment control.
          'flex h-auto w-full flex-wrap justify-start gap-1 rounded-md border',
          'border-border bg-warm-bg p-1 text-sm text-muted-foreground',
        )}
      >
        {tabs.map((t) => (
          <TabsTrigger
            key={t.slug}
            value={t.slug}
            className={cn(
              'flex items-center gap-1 whitespace-nowrap rounded px-3 py-1.5',
              'text-sm font-medium text-muted-foreground',
              'hover:text-foreground',
              'data-[state=active]:bg-coral data-[state=active]:text-white',
              'data-[state=active]:shadow-sm',
            )}
          >
            <span>{t.label}</span>
            <CountBadge count={counts[t.slug]} />
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export default ManagementCategoryBar;
